import React, { useState } from 'react';
import { Header } from './components/Header';
import { Navigation } from './components/Navigation';
import { AudioUnlockModal } from './components/AudioUnlockModal';
import { AcousticLegendModal } from './components/AcousticLegendModal';
import { AudioEngine } from './audio/AudioEngine';

// AR6 Figure Modules
import { SPM1History } from './components/figures/SPM1History';
import { SPM2Drivers } from './components/figures/SPM2Drivers';
import { SPM3Extremes } from './components/figures/SPM3Extremes';
import { SPM4Scenarios } from './components/figures/SPM4Scenarios';
import { SPM5Spatial } from './components/figures/SPM5Spatial';
import { SPM6Cumulative } from './components/figures/SPM6Cumulative';
import { SPM7Frequencies } from './components/figures/SPM7Frequencies';
import { SPM8SeaLevel } from './components/figures/SPM8SeaLevel';
import { SPM9CIDMatrix } from './components/figures/SPM9CIDMatrix';
import { SPM10CarbonBudget } from './components/figures/SPM10CarbonBudget';

// Grand Symphony View
import { GrandSymphonyView } from './components/GrandSymphonyView';

// SR15 Figure Modules
import { SR15SPM1Warming } from './components/figures/SR15SPM1Warming';
import { SR15SPM2Pathways } from './components/figures/SR15SPM2Pathways';
import { SR15SPM3Risks } from './components/figures/SR15SPM3Risks';
import { SR15SPM4SDGs } from './components/figures/SR15SPM4SDGs';

export function App() {
  const [activeReport, setActiveReport] = useState<'ar6' | 'sr15'>('ar6');
  const [activeFigureId, setActiveFigureId] = useState<string>('symphony');
  const [isGlobalPlaying, setIsGlobalPlaying] = useState<boolean>(false);
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState<boolean>(true);
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false);
  const [comparativeWarming, setComparativeWarming] = useState<'1.5' | '2.0' | null>(null);

  const audioEngine = AudioEngine.getInstance();

  const handleSelectReport = (report: 'ar6' | 'sr15') => {
    setActiveReport(report);
    setActiveFigureId(report === 'ar6' ? 'symphony' : 'sr15_spm1');
    setIsGlobalPlaying(false);
    audioEngine.stopAll();
  };

  const handleSelectFigure = (id: string) => {
    setActiveFigureId(id);
    setIsGlobalPlaying(false);
    audioEngine.stopAll();
  };

  const handleToggleGlobalPlay = async () => {
    if (!audioEngine.getIsUnlocked()) {
      await audioEngine.unlock();
    }
    const nextState = !isGlobalPlaying;
    setIsGlobalPlaying(nextState);
    if (!nextState) {
      audioEngine.stopAll();
    }
  };

  const handleToggleComparativeWarming = (val: '1.5' | '2.0') => {
    setComparativeWarming(val);
    if (activeFigureId !== 'sr15_spm3') {
      setActiveFigureId('sr15_spm3');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100 selection:bg-cyan-500 selection:text-slate-950">
      {/* Header */}
      <Header
        activeReport={activeReport}
        onSelectReport={handleSelectReport}
        isGlobalPlaying={isGlobalPlaying}
        onToggleGlobalPlay={handleToggleGlobalPlay}
        onOpenLegend={() => setIsLegendOpen(true)}
        comparativeWarming={comparativeWarming}
        onToggleComparativeWarming={handleToggleComparativeWarming}
      />

      {/* Figures Selector Navigation */}
      <Navigation
        activeReport={activeReport}
        activeFigureId={activeFigureId}
        onSelectFigure={handleSelectFigure}
      />

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
        {/* AR6 WGI Figures & Grand Symphony Suite */}
        {activeReport === 'ar6' && (
          <>
            {activeFigureId === 'symphony' && (
              <GrandSymphonyView
                isGlobalPlaying={isGlobalPlaying}
                onToggleGlobalPlay={handleToggleGlobalPlay}
                onSelectFigure={handleSelectFigure}
              />
            )}
            {activeFigureId === 'spm1' && <SPM1History isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm2' && <SPM2Drivers isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm3' && <SPM3Extremes isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm4' && <SPM4Scenarios isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm5' && <SPM5Spatial isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm6' && <SPM6Cumulative isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm7' && <SPM7Frequencies isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm8' && <SPM8SeaLevel isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm9' && <SPM9CIDMatrix isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'spm10' && <SPM10CarbonBudget isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
          </>
        )}

        {/* SR15 Figures */}
        {activeReport === 'sr15' && (
          <>
            {activeFigureId === 'sr15_spm1' && <SR15SPM1Warming isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'sr15_spm2' && <SR15SPM2Pathways isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
            {activeFigureId === 'sr15_spm3' && (
              <SR15SPM3Risks
                isPlaying={isGlobalPlaying}
                onTogglePlay={handleToggleGlobalPlay}
                comparativeWarming={comparativeWarming}
              />
            )}
            {activeFigureId === 'sr15_spm4' && <SR15SPM4SDGs isPlaying={isGlobalPlaying} onTogglePlay={handleToggleGlobalPlay} />}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="w-full glass-panel border-t border-slate-800/80 py-6 px-4 text-center text-xs text-slate-500 space-y-2">
        <p>
          IPCC Sixth Assessment Report (AR6 WGI, 2021) &bull; IPCC Special Report on Global Warming of 1.5°C (SR15, 2018)
        </p>
        <p className="text-[11px] text-slate-600">
          Client-side Web Audio Sonification Suite &bull; Zero Backend External Audio Files &bull; Optimized for Static Deployment on Cloudflare Pages
        </p>
      </footer>

      {/* Modals */}
      <AudioUnlockModal
        isOpen={isUnlockModalOpen}
        onUnlocked={() => setIsUnlockModalOpen(false)}
      />

      <AcousticLegendModal
        isOpen={isLegendOpen}
        onClose={() => setIsLegendOpen(false)}
      />
    </div>
  );
}

export default App;
